import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  CommandPaletteProvider,
  DEFAULT_COMMAND_GROUP_ORDER,
  ConfirmProvider,
  FormattersProvider,
  DsI18nProvider,
  type DsTranslator,
  HotkeysProvider,
  KeyboardShortcutSheetProvider,
  LiveUpdatesProvider,
  MapProviderProvider,
  PreferencesProvider,
  ReferenceProvider,
  PlaceProvider,
  NextActionExtraProvider,
  PickerManageListProvider,
  schematicMapProvider,
  StatusLabelProvider,
  EnablementProvider,
  ListModeProvider,
  type ListMode,
  type Requirement as EnablementRequirement,
  ThemeProvider,
  ToastI18nBridge,
  ToastProvider,
  usePreference,
  useTheme,
  useToast,
  type Density,
  type Theme,
  type UnitSystem,
} from '@zagru/design-system';
import { createQueryClient, describeError, isLocale, loadLocale, type Locale, sessionStore, setTranslationOverrides, setDiagnosticContext, useApiClient, useLocale, usePermissions, useT, ZagruPlatformProvider, type ZagruProblem } from '@zagru/platform';
import { defaultDensity } from '@/app/navigation/navConfig';
import { userScopedKey } from '@/features/shared/userStorage';
import { isDensityChosen, localeStore, markDensityChosen, problemStore, setPendingReturnTo } from '@/app/state/appStores';
import { MetadataFragmentsProvider } from '@/app/metadata/MetadataFragmentsProvider';
import { I18nOverlayBridge } from '@/app/enablement/I18nOverlayBridge';
import { useEnablementCheck, type Requirement } from '@/api/enablement';
import { liveSource } from '@/api/live';
import * as platform from '@/api/platform';
import { createRefdataSource } from '@/api/refdata';
import { createPlaceSource, toCountryAddressSchema, useCountrySchemas, useDisplayFormats, userPlaceFormatKeys } from '@/api/geo';
import { createAppPreferencesStore } from './preferencesStore';

export interface AppProvidersProps {
  children: ReactNode;
  /** Query client override (tests). */
  queryClient?: QueryClient;
}

setDiagnosticContext(() => ({ tenantId: sessionStore.getState().tenant?.id }));

/** Keys of the shell preferences (D-009). */
export const uiPreferenceKeys = {
  theme: 'theme',
  density: 'density',
  locale: 'locale',
  timeZone: 'timeZone',
  units: 'units',
  navCollapsed: 'nav.collapsed',
  /** Wave 4 (D-507, ADR-0503): table / card / auto for every list. */
  listMode: 'listMode',
  /** Wave 4 (spec §7): a BCP-47 region for formats (`de-DE`), or null to follow the language. */
  regionalFormat: 'regionalFormat',
  /** Wave 4 (ADR-0503): the sidebar's pinned lenses and views. */
  pinned: 'pinned',
} as const;

/**
 * Toasts are transient feedback, not the channel for failures. A failed read is shown where the data would have
 * been (ErrorState, degraded widget); a validation or conflict problem is shown by the form. Only a failed write
 * that its screen does not render inline gets a short toast — and never with diagnostics in it.
 */
function shouldToast(problem: ZagruProblem, kind: 'query' | 'mutation', handledInline: boolean): boolean {
  if (kind === 'query' || handledInline) return false;
  if (problem.isUnauthorized()) return false;
  if (problem.status === 400 || problem.status === 422 || problem.status === 409) return false;
  if (problem.code === 'client.aborted') return false;
  return true;
}

/** Applies the role profile's default density until the user picks one explicitly. */
function ProfileDensity(): null {
  const profile = sessionStore((s) => s.uiProfile);
  const status = sessionStore((s) => s.status);
  const { density, setDensity } = useTheme();
  useEffect(() => {
    if (status !== 'ready' || isDensityChosen()) return;
    const wanted = defaultDensity[profile];
    if (wanted !== density) {
      setDensity(wanted);
      // `setDensity` persists the value; clear it again so the profile default keeps applying on profile changes.
      try {
        localStorage.removeItem('zagru.density');
      } catch {
        /* ignore */
      }
    }
  }, [status, profile, density, setDensity]);
  return null;
}

/**
 * Closes the live change stream (G-003) when the user signs out, so the next person on this browser never
 * receives a frame opened under the previous session; the stream reopens on the next subscription.
 */
function LiveSessionGuard(): null {
  const userId = sessionStore((s) => s.user?.id);
  const previous = useRef(userId);
  useEffect(() => {
    if (previous.current && previous.current !== userId) liveSource.close();
    previous.current = userId;
  }, [userId]);
  return null;
}

const themes: Theme[] = ['light', 'dark', 'system'];
const densities: Density[] = ['comfortable', 'compact', 'touch'];

/**
 * Theme and density live in the preferences store (D-009) and in the ThemeProvider at once: the provider keeps
 * `zagru.theme` / `zagru.density` in localStorage for the pre-React bootstrap, the store carries the choice to
 * the next device. A stored preference wins on arrival; a change through `useTheme` is written back.
 */
/**
 * W12: the tenant's own wording of the interface (Settings › Localization › Review) is laid over the shipped
 * catalogue of the language being viewed. Review marks change nothing on screen, so only overrides are applied.
 */
/**
 * L-01 (ADR-0047): text the server resolves for the viewer's language (process experiences, shipment profiles, task
 * titles) arrives through `Accept-Language`. When the language changes, the queries on screen are fetched again in
 * the background — what is shown stays until the new answer is there.
 */
function ServerLanguageSync(): null {
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  const previous = useRef(locale);
  useEffect(() => {
    if (previous.current === locale) return;
    previous.current = locale;
    void queryClient.invalidateQueries({ refetchType: 'active' });
  }, [locale, queryClient]);
  return null;
}

function TranslationOverridesSync(): null {
  const { locale } = useLocale();
  const signedIn = sessionStore((s) => !!s.user);
  const overrides = platform.useTranslations({ namespace: platform.UI_TRANSLATION_NAMESPACE, locale }, { enabled: signedIn, staleTime: 5 * 60_000, retry: false });
  useEffect(() => {
    if (!overrides.data || !isLocale(locale)) return;
    setTranslationOverrides(locale, Object.fromEntries(overrides.data.filter((r) => r.status === 'OVERRIDDEN').map((r) => [r.key, r.text])));
  }, [overrides.data, locale]);
  return null;
}

function ThemePreferenceSync(): null {
  const { theme, density, setTheme, setDensity } = useTheme();
  const [storedTheme, setStoredTheme, themeMeta] = usePreference<Theme | null>(uiPreferenceKeys.theme, null);
  const [storedDensity, setStoredDensity, densityMeta] = usePreference<Density | null>(uiPreferenceKeys.density, null);
  const applied = useRef(false);

  useEffect(() => {
    if (themeMeta.loading || densityMeta.loading || applied.current) return;
    applied.current = true;
    if (storedTheme && themes.includes(storedTheme) && storedTheme !== theme) setTheme(storedTheme);
    if (storedDensity && densities.includes(storedDensity) && storedDensity !== density) {
      markDensityChosen();
      setDensity(storedDensity);
    }
  }, [themeMeta.loading, densityMeta.loading, storedTheme, storedDensity, theme, density, setTheme, setDensity]);

  useEffect(() => {
    if (!applied.current) return;
    if (storedTheme !== theme) void setStoredTheme(theme);
  }, [theme, storedTheme, setStoredTheme]);
  useEffect(() => {
    if (!applied.current || !isDensityChosen()) return;
    if (storedDensity !== density) void setStoredDensity(density);
  }, [density, storedDensity, setStoredDensity]);
  return null;
}

/**
 * Locale, time zone and units for `useFormatters()`, from the session user and the viewer's preferences — and the
 * design system's own words through the same catalogue (C3; a key the catalogue lacks keeps the component's English).
 */
function Formatters({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const t = useT();
  const dsTranslate = useCallback<DsTranslator>((key, params) => {
    const text = t(key, params);
    return text === key ? undefined : text;
  }, [t]);
  const user = sessionStore((s) => s.user);
  const [timeZone] = usePreference<string | null>(uiPreferenceKeys.timeZone, null);
  const [units] = usePreference<UnitSystem>(uiPreferenceKeys.units, 'metric');
  // Wave 4 (spec §7 Formats): dates, numbers and money may follow a region other than the language (English with
  // German formats); unset, they follow the language.
  const [regionalFormat] = usePreference<string | null>(uiPreferenceKeys.regionalFormat, null);
  return (
    <FormattersProvider locale={regionalFormat ?? locale} timeZone={timeZone ?? user?.timeZone} units={units}>
      <DsI18nProvider translate={dsTranslate}>
        {/* L-13c (G-002): points with coordinates go on a schematic map; a tiled provider replaces it once a tile vendor is chosen. */}
        <MapProviderProvider provider={schematicMapProvider}>{children}</MapProviderProvider>
      </DsI18nProvider>
    </FormattersProvider>
  );
}

/**
 * D-020: every `ReferencePicker` / `ReferenceDisplay` reads the governed catalogue through one cached source per
 * tenant and language (a language switch or another tenant starts a fresh cache).
 */
function ReferenceData({ children }: { children: ReactNode }) {
  const client = useApiClient();
  const { locale } = useLocale();
  const tenantId = sessionStore((s) => s.tenant?.id);
  const lang = locale.split('-')[0] ?? 'en';
  const source = useMemo(() => createRefdataSource(client, { tenantId, lang }), [client, tenantId, lang]);
  useEffect(() => () => source.dispose(), [source]);
  return <ReferenceProvider source={source}>{children}</ReferenceProvider>;
}

/**
 * D-021: every `PlacePicker` / `PlaceDisplay` reads places through one cached source per tenant and language, and
 * writes them with the display formats resolved user › role › party › tenant › platform — the viewer's own choice
 * (Preferences) on top (D-032), the country's own default when nothing is set.
 */
/** The command palette in the viewer's language: prompt, empty message and the group headings. */
function Palette({ recentKey, children }: { recentKey: string; children: ReactNode }) {
  const t = useT();
  // W27: the one-line entry's "Create a shipment: …" answers first.
  const paletteGroupOrder = useMemo(() => ['Create', ...DEFAULT_COMMAND_GROUP_ORDER], []);
  const groupLabel = useMemo(
    () => (group: string) => {
      const key = `shell.palette.group.${group.toLowerCase()}`;
      const text = t(key);
      return text === key ? group : text;
    },
    [t],
  );
  return (
    <CommandPaletteProvider shortcut={false} recentKey={recentKey} placeholder={t('shell.palette.placeholder')} emptyMessage={t('shell.palette.empty')} maxRecent={6} groupLabel={groupLabel} groupOrder={paletteGroupOrder}>
      {children}
    </CommandPaletteProvider>
  );
}

/** Status badges and next actions read the i18n bundle (`status.<entity>.<VALUE>`), the registry's English otherwise. */
function StatusLabels({ children }: { children: ReactNode }) {
  const t = useT();
  const resolve = useMemo(
    () => (labelKey: string | undefined, fallback: string) => {
      if (!labelKey) return fallback;
      const text = t(labelKey);
      return text === labelKey ? fallback : text;
    },
    [t],
  );
  // Next actions read in the viewer's language wherever they are drawn (the builders take `extra.t`).
  const nextActionExtra = useMemo(() => ({ t }), [t]);
  // W29: a reference-data picker's "Manage this list →" opens the list's own page, for those who may change it.
  const { can } = usePermissions();
  const mayManage = can('refdata:catalog:manage');
  const mayManagePlaces = can('geo:place:manage');
  // W10: `place` is the place graph, `lookup:<code>` a governed lookup list, anything else a reference-data kind.
  const manageList = useMemo(
    () => (kind: string) => {
      if (kind === 'place') return mayManagePlaces ? { href: '/settings/locations' } : undefined;
      if (!mayManage) return undefined;
      return { href: kind.startsWith('lookup:') ? `/settings/lookups/${encodeURIComponent(kind.slice(7))}` : `/settings/reference-data/${encodeURIComponent(kind)}` };
    },
    [mayManage, mayManagePlaces],
  );
  // Wave 4 (D-503): design-system surfaces (columns, filters, views, menus, form sections) read what is enabled.
  const enablementCheck = useEnablementCheck();
  const [listMode] = usePreference<ListMode>(uiPreferenceKeys.listMode, 'auto');
  const isEnabled = useCallback((requirement: EnablementRequirement) => enablementCheck(requirement as Requirement), [enablementCheck]);
  return (
    <StatusLabelProvider resolve={resolve}>
      <EnablementProvider isEnabled={isEnabled}>
        <ListModeProvider mode={listMode}>
          <NextActionExtraProvider extra={nextActionExtra}>
            <PickerManageListProvider resolve={manageList}>{children}</PickerManageListProvider>
          </NextActionExtraProvider>
        </ListModeProvider>
      </EnablementProvider>
    </StatusLabelProvider>
  );
}

function Places({ children }: { children: ReactNode }) {
  const client = useApiClient();
  const { locale } = useLocale();
  const tenantId = sessionStore((s) => s.tenant?.id);
  const userId = sessionStore((s) => s.user?.id);
  const roleCodes = sessionStore((s) => s.roles).map((r) => r.code);
  const ready = sessionStore((s) => s.status) === 'ready';
  const lang = locale.split('-')[0] ?? 'en';
  const source = useMemo(() => createPlaceSource(client, { tenantId, lang }), [client, tenantId, lang]);
  useEffect(() => () => source.dispose(), [source]);
  const formats = useDisplayFormats({ roleCodes, userId }, { enabled: ready });
  const schemas = useCountrySchemas({ enabled: ready });
  const [userShort] = usePreference<string>(userPlaceFormatKeys.short, '');
  const [userLong] = usePreference<string>(userPlaceFormatKeys.long, '');
  const [userPicker] = usePreference<string>(userPlaceFormatKeys.picker, '');
  const [userDocument] = usePreference<string>(userPlaceFormatKeys.document, '');
  const t = useT();
  const labels = useMemo(() => {
    const keys = ['placeholder', 'recent', 'empty', 'emptyHint', 'noResults', 'noResultsHint', 'searching', 'drillHint', 'up', 'clear', 'create', 'useAddress', 'resolving', 'resolvedTitle', 'unmatched', 'widen'] as const;
    const groups = ['sites', 'own', 'countries', 'regions', 'cities', 'districts', 'postal', 'ports', 'addresses'];
    const types = ['continent', 'country', 'admin1', 'admin2', 'admin3', 'city', 'district', 'neighbourhood', 'postal_code', 'port', 'airport', 'rail_terminal', 'inland_terminal', 'border_crossing', 'unlocode', 'warehouse', 'zone', 'location', 'dock', 'yard_slot', 'address', 'custom'];
    // `{query}` style placeholders are filled by the picker, so they must survive translation untouched.
    const raw = (key: string) => t(key, { query: '{query}', name: '{name}', parts: '{parts}' });
    return {
      ...Object.fromEntries(keys.map((k) => [k, raw(`geo.picker.${k}`)])),
      groups: Object.fromEntries(groups.map((g) => [g, t(`geo.picker.group.${g}`)])),
      types: Object.fromEntries(types.map((ty) => [ty, t(`settings.places.type.${ty}`)])),
    };
  }, [t]);
  const value = useMemo(() => {
    const server = Object.fromEntries((formats.data ?? []).map((f) => [f.context, f.template]));
    const templates = {
      short: userShort || server.short || '',
      long: userLong || server.long || '',
      picker: userPicker || server.picker || '',
      document: userDocument || server.document || '',
    };
    const byCountry = new Map((schemas.data ?? []).map((s) => [s.countryCode, toCountryAddressSchema(s)] as const));
    return { templates, lang, labels, schemaFor: (cc: string | null | undefined) => byCountry.get((cc ?? '').toUpperCase()) ?? byCountry.get('*') };
  }, [formats.data, schemas.data, userShort, userLong, userPicker, userDocument, lang, labels]);
  return (
    <PlaceProvider source={source} formats={value}>
      {children}
    </PlaceProvider>
  );
}

/** The preferences store for the signed-in user: server + local mirror once the session is ready. */
function Preferences({ children }: { children: ReactNode }) {
  const client = useApiClient();
  const userId = sessionStore((s) => s.user?.id);
  const status = sessionStore((s) => s.status);
  const store = useMemo(() => createAppPreferencesStore(status === 'ready' ? client : null, userId), [client, userId, status]);
  return <PreferencesProvider store={store}>{children}</PreferencesProvider>;
}

function PlatformRoot({ children, queryClient }: AppProvidersProps): ReactElement {
  const { toast } = useToast();
  const t = useT();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const tRef = useRef(t);
  tRef.current = t;

  const [ownQueryClient] = useState(
    () =>
      queryClient ??
      createQueryClient({
        onProblem: (problem, context) => {
          problemStore.getState().record(problem);
          if (!shouldToast(problem, context.kind, context.handledInline)) return;
          const described = describeError(problem, tRef.current, { action: 'save' });
          toastRef.current.error(described.title, {
            id: `problem-${problem.status}-${problem.code ?? 'unknown'}`,
            description: described.description,
          });
        },
      }),
  );

  const chosenLocale = localeStore((s) => s.chosen);
  const userLocale = sessionStore((s) => s.user?.locale);
  // Wave 4 (ADR-0503 §6): at sign-in the profile's language wins, so it follows the user across devices; the browser's
  // `zagru.locale` is only the offline cache for the first paint. A switch writes both, so they agree afterwards.
  const [syncedUserLocale, setSyncedUserLocale] = useState<string | undefined>(undefined);
  if (userLocale !== syncedUserLocale) {
    setSyncedUserLocale(userLocale);
    // `zh-Hans` is a locale of its own; `tr-TR` means `tr`.
    const serverLocale = [userLocale, userLocale?.split('-')[0]].find((l): l is Locale => !!l && isLocale(l));
    if (serverLocale && serverLocale !== chosenLocale) {
      void loadLocale(serverLocale).then(() => localeStore.getState().setChosen(serverLocale));
    }
  }
  /*
   * The palette's recents are the viewer's, not the browser's.
   *
   * A recent entry stores the label and the href of what was opened — "ORD-400112 · Meridian Auto Parts"
   * at `/outbound/orders/ord-112`. Under a shared key the next person to sign in on the same machine opened
   * the palette and saw the last person's customers and orders waiting for them. Same rule, and same reason,
   * as the grid state and the remembered warehouse.
   */
  const userId = sessionStore((s) => s.user?.id);
  const locale = chosenLocale ?? (isLocale(userLocale) ? userLocale : undefined);

  return (
    <ZagruPlatformProvider queryClient={ownQueryClient} locale={locale} onAuthenticated={(returnTo) => setPendingReturnTo(returnTo ?? null)}>
      <Preferences>
        <ThemePreferenceSync />
        <TranslationOverridesSync />
        {/* W2: the terminology overlays of the tenant's enabled packs and apps (spec §4.4). */}
        <I18nOverlayBridge />
        <ServerLanguageSync />
        <Formatters>
          <ToastI18nBridge />
          <ReferenceData>
          <Places>
          <StatusLabels>
          <HotkeysProvider>
            <ConfirmProvider>
              <LiveUpdatesProvider source={liveSource}>
                <LiveSessionGuard />
                {/* mod+K is registered through the hotkeys service by the shell, so the `?` sheet lists it. */}
                <Palette recentKey={userScopedKey(userId, 'commands.recent')}>
                  <KeyboardShortcutSheetProvider>
                    <ProfileDensity />
                    <MetadataFragmentsProvider>{children}</MetadataFragmentsProvider>
                  </KeyboardShortcutSheetProvider>
                </Palette>
              </LiveUpdatesProvider>
            </ConfirmProvider>
          </HotkeysProvider>
          </StatusLabels>
          </Places>
          </ReferenceData>
        </Formatters>
      </Preferences>
    </ZagruPlatformProvider>
  );
}

/**
 * Provider stack: Theme → Toast → Platform (Query, ApiClient, Telemetry, I18n, Auth, Session, FeatureFlags) →
 * Preferences → Formatters → Hotkeys → Confirm → LiveUpdates → CommandPalette → KeyboardShortcutSheet.
 * The router is rendered by the caller inside this tree; the URL-state adapter and the global shortcuts live in
 * the shell, where the router hooks are available.
 */
export function AppProviders({ children, queryClient }: AppProvidersProps): ReactElement {
  return (
    <ThemeProvider>
      <ToastProvider>
        <PlatformRoot queryClient={queryClient}>{children}</PlatformRoot>
      </ToastProvider>
    </ThemeProvider>
  );
}
